/**
 * SQLite-specific database queries
 *
 * Notes:
 * - SQLite is file-based (no real server databases/schemas in the same sense).
 * - Explorer treats the connection as a single database context ("main").
 */

function escapeSqliteStringLiteral(value: string): string {
  return value.replace(/'/g, "''");
}

export const sqliteQueries = {
  /** SQLite has a single primary database context by default. */
  databases: `SELECT 'main' AS name;`,

  /** SQLite has no schema concept — mirrors the `'' AS schema_name` the table listing reports. */
  schemas: `SELECT '' AS name;`,

  /** Get tables for the database (exclude internal sqlite_* tables). */
  tables: `
    SELECT
      m.name AS id,
      m.name,
      '' AS schema_name,
      'table' AS object_type
    FROM
      sqlite_master m
    WHERE
      m.type = 'table'
      AND m.name NOT LIKE 'sqlite_%'
    ORDER BY
      m.name;
  `,

  /** Get views for the database (exclude internal sqlite_* objects). */
  views: `
    SELECT
      m.name AS id,
      m.name,
      '' AS schema_name,
      'view' AS object_type
    FROM
      sqlite_master m
    WHERE
      m.type = 'view'
      AND m.name NOT LIKE 'sqlite_%'
    ORDER BY
      m.name;
  `,

  /** SQLite has no stored procedures. Keep contract shape, return no rows. */
  storedProcedures: `
    SELECT
      '' AS id,
      '' AS name,
      '' AS schema_name,
      'stored-procedure' AS object_type
    WHERE 0;
  `,

  /** SQLite has no functions in the same catalog sense. Keep contract shape, return no rows. */
  functions: `
    SELECT
      '' AS id,
      '' AS name,
      '' AS schema_name,
      'function' AS object_type
    WHERE 0;
  `,

  /**
   * Get columns for a table or view.
   * Uses PRAGMA as recommended in Docs/DbEngineSQLite.md.
   */
  columns: (_schema: string, tableName: string) =>
    `PRAGMA table_info('${escapeSqliteStringLiteral(tableName)}');`,

  /** Not used for SQLite yet (Slice 3 scope). Keep contract shape, return no rows. */
  tableConstraints: (_schema: string, _tableName: string) => {
    void _schema;
    void _tableName;

    return `
      SELECT
        '' AS constraint_name,
        '' AS constraint_type,
        '' AS column_name,
        '' AS referenced_constraint,
        '' AS referenced_schema,
        '' AS referenced_table,
        '' AS referenced_column,
        '' AS check_definition
      WHERE 0;
    `;
  },

  /** Not used for SQLite yet (Slice 3 scope). Keep contract shape, return no rows. */
  tableIndexes: (_schema: string, _tableName: string) => {
    void _schema;
    void _tableName;

    return `
      SELECT
        '' AS index_name,
        '' AS column_name,
        0 AS is_unique,
        '' AS index_type,
        0 AS key_ordinal
      WHERE 0;
    `;
  },

  /**
   * Get table triggers for a table.
   * SQLite stores trigger info in sqlite_master.
   */
  tableTriggers: (_schema: string, tableName: string) => `
    SELECT
      name AS trigger_name,
      '' AS trigger_timing,
      '' AS trigger_events,
      1 AS is_enabled,
      sql AS trigger_sql
    FROM sqlite_master
    WHERE type = 'trigger'
      AND tbl_name = '${escapeSqliteStringLiteral(tableName)}'
    ORDER BY name;
  `,

  /** SQLite doesn't have named default constraints. Return empty result set. */
  tableDefaultConstraints: (_schema: string, _tableName: string) => {
    void _schema;
    void _tableName;

    return `
      SELECT
        '' AS constraint_name,
        'DEFAULT' AS constraint_type,
        '' AS column_name,
        '' AS default_definition
      WHERE 0;
    `;
  },

  /** SQLite doesn't have a statistics catalog. Return empty result set. */
  tableStatistics: (_schema: string, _tableName: string) => {
    void _schema;
    void _tableName;

    return `
      SELECT
        '' AS statistic_name,
        '' AS column_name,
        0 AS column_ordinal,
        0 AS auto_created,
        0 AS user_created,
        NULL AS last_updated
      WHERE 0;
    `;
  },

  /** Minimal placeholder for shared UI; SQLite doesn't expose these properties like MSSQL/PGSQL. */
  databaseProperties: (databaseName: string) => `
    SELECT
      '${escapeSqliteStringLiteral(databaseName)}' AS database_name,
      NULL AS collation,
      NULL AS owner,
      NULL AS recovery_model,
      NULL AS compatibility_level;
  `,

  /** Default database for SQLite connections. */
  defaultDatabase: 'main',

  /** Fallback database list for SQLite. */
  fallbackDatabases: ['main'],
};
